import React, { useState, useEffect } from "react";
import {
  Box,
  Avatar,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Checkbox,
  FormControlLabel,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import PageHeader from "../components/PageHeader";
import CountryCodePhoneField from "../components/CountryCodePhoneField";
import { useNavigate } from "react-router-dom";
import CustomTextField from "../components/CustomTextField";
import { COLORS } from "../assets/theme";
import GenericFormDialog from "../components/GenericFormDialog";
import {
  updateMyProfile,
  changePassword,
  getMyProfile,
} from "../services/UsersService";
import { useUser } from "../contexts/UserContext";
import { toast } from "react-toastify";
import { disableCurrentBrowserWebPush } from "../services/backofficeWebPush";
import { clearTokens } from "../services/authService";

const showErrorToast = (message: any) =>
  toast.error(String(message), { toastId: String(message) });

const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;
const initialProfile = {
  fullName: "",
  email: "",
  phone: "",
  country: "",
  image: "",
};

const mapUserToProfile = (user: any) => ({
  fullName: user?.fullName || "",
  email: user?.email || "",
  phone: user?.phone || "",
  country: user?.country || "",
  image: user?.banner || "",
});

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(initialProfile);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(initialProfile);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [disablingNotifications, setDisablingNotifications] = useState(false);

  const { setUser } = useUser();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);

        const data = await getMyProfile();
        const mapped = mapUserToProfile(data);

        setProfile(mapped);
        setForm(mapped);

        if (setUser) {
          setUser({
            ...data,
            email: data.email ?? undefined,
            banner: data.banner ?? undefined,
            phone: data.phone ?? undefined,
            country: data.country ?? undefined,
            role: data.role ?? undefined,
          });
        }
      } catch (err: any) {
        console.error("Error fetching profile:", err);
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load profile";
        showErrorToast(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [setUser]);

  const DEFAULT_IMAGE = "/avatar-placeholder.png";

  const passwordFields = [
    {
      type: "password" as const,
      name: "oldPassword",
      label: "Old Password",
      required: true,
      showPassword: true,
    },
    {
      type: "password" as const,
      name: "newPassword",
      label: "New Password",
      required: true,
      showPassword: true,
    },
    {
      type: "password" as const,
      name: "confirmPassword",
      label: "Confirm Password",
      required: true,
      showPassword: true,
    },
    {
      name: "logoutAllDevices",
      label: "Log out of all devices",
      colSpan: 2 as const,
      customRender: (value: boolean, onChange: (value: boolean) => void) => (
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(value)}
              onChange={(event) => onChange(event.target.checked)}
            />
          }
          label={
            <Box>
              <Typography>Log out of all devices</Typography>
              <Typography variant="body2" color="text.secondary">
                You’ll be signed out everywhere, including this device. You’ll
                need to sign in again.
              </Typography>
            </Box>
          }
          sx={{ alignItems: "flex-start", m: 0 }}
        />
      ),
    },
  ];

  const handleChangePassword = () => {
    setPasswordDialogOpen(true);
  };

  const handlePasswordDialogSubmit = async (values: Record<string, any>) => {
    if (changingPassword) return;

    const currentPassword = values.oldPassword?.trim();
    const newPassword = values.newPassword?.trim();
    const confirmPassword = values.confirmPassword?.trim();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill in all password fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New password and confirmation do not match.");
      return;
    }

    setChangingPassword(true);

    try {
      const response = await changePassword({
        oldPassword: currentPassword,
        newPassword,
        confirmPassword,
        logoutAllDevices: Boolean(values.logoutAllDevices),
      });

      setPasswordDialogOpen(false);
      if (response.loggedOutAllDevices) {
        clearTokens();
        setUser(null);
        navigate("/login", { replace: true });
        return;
      }

      toast.success(response.message || "Password updated successfully.");
    } catch (err: any) {
      const apiErrorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;

      toast.error(apiErrorMessage || "Failed to change password.");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    try {
      const maybeFile = (form as any).imageFile;
      const maybeImage = (form as any).image;
      if (maybeFile instanceof File && typeof maybeImage === "string") {
        if (maybeImage.startsWith("blob:")) {
          URL.revokeObjectURL(maybeImage);
        }
      }
    } catch (e) {
      // ignore
    }

    setForm((prev) => ({ ...(profile as any) } as typeof prev));
    setEditMode(false);
    setRemoveImage(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      const currentFullName = form.fullName.trim();
      if (!currentFullName) {
        toast.error("Full name is required.");
        setSaving(false);
        return;
      }

      const payload: {
        full_name?: string;
        email?: string;
        phone?: string;
        country?: string;
        banner?: string;
      } = {};

      if (currentFullName !== (profile.fullName || "").trim()) {
        payload.full_name = currentFullName;
      }

      if ((form.phone || "") !== (profile.phone || "")) {
        payload.phone = form.phone;
      }

      if ((form.email || "").trim() !== (profile.email || "").trim()) {
        payload.email = (form.email || "").trim();
      }

      if ((form.country || "") !== (profile.country || "")) {
        payload.country = form.country;
      }

      if (removeImage) {
        payload.banner = "";
      }

      const selectedImageFile = (form as any).imageFile as File | undefined;

      if (Object.keys(payload).length === 0 && !selectedImageFile) {
        toast.info("No changes to save.");
        setSaving(false);
        return;
      }

      await updateMyProfile(payload, selectedImageFile);

      const refreshed = await getMyProfile();
      const updatedProfile = mapUserToProfile(refreshed);

      setProfile(updatedProfile);
      setForm(updatedProfile);

      if (setUser) {
        setUser({
          ...refreshed,
          email: refreshed.email ?? undefined,
          banner: refreshed.banner ?? undefined,
          phone: refreshed.phone ?? undefined,
          country: refreshed.country ?? undefined,
          role: refreshed.role ?? undefined,
        });
      }

      setEditMode(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error("Error saving profile:", err);
      const msg =
        err?.response?.data?.message || err?.response?.data?.error || "Failed to save profile";
      showErrorToast(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDisableNotificationsOnThisDevice = async () => {
    if (disablingNotifications) return;

    setDisablingNotifications(true);
    try {
      const result = await disableCurrentBrowserWebPush();

      if (result.status === "disabled") {
        toast.success("Notifications disabled on this device.");
        return;
      }

      if (result.status === "missing-subscription") {
        toast.info("Notifications are not enabled on this browser.");
        return;
      }

      if (result.status === "unsupported") {
        toast.info("This browser does not support web push notifications.");
        return;
      }

      toast.info("Browser notifications were removed locally.");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to disable notifications on this device.",
      );
    } finally {
      setDisablingNotifications(false);
    }
  };

  const shownImage = editMode ? form.image : profile.image;
  const shownName = (editMode ? form.fullName : profile.fullName) || "";
  const initial = shownName.trim().charAt(0).toUpperCase() || "U";
  const openImagePicker = () => {
    if (editMode) {
      document.getElementById("profile-image-input")?.click();
    }
  };
  const cardSx = {
    bgcolor: COLORS.WhiteBackground,
    borderRadius: 4,
    border: "1px solid",
    borderColor: "divider",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    overflow: "hidden",
  };
  const fieldSx = {
    fontSize: { xs: "0.95rem", sm: "1rem" },
    backgroundColor: COLORS.textFieldBackgroundColor,
  };
  const actionRowSx = {
    display: "flex",
    alignItems: { xs: "stretch", sm: "center" },
    flexDirection: { xs: "column", sm: "row" },
    gap: 2,
    py: 2,
    px: { xs: 2, sm: 3 },
  };
  const rowIconSx = {
    width: 40,
    height: 40,
    borderRadius: 2.5,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: alpha(COLORS.MainColor, 0.08),
    color: COLORS.MainColor,
  };
  const buttonSx = { textTransform: "none", fontWeight: 600, borderRadius: 2.5 };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: COLORS.MainBackground }}>
      <Box
        sx={{
          maxWidth: 880,
          mx: "auto",
          px: { xs: 2, sm: 4 },
          py: { xs: 3, sm: 5 },
        }}
      >
        <PageHeader
          title="My Profile"
          subtitle="Your account details, password and notifications."
          icon={<PersonOutlineIcon />}
          actions={
            !loading && !editMode ? (
              <>
                <Button
                  variant="text"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(-1)}
                  sx={buttonSx}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<EditOutlinedIcon />}
                  onClick={handleEdit}
                  sx={buttonSx}
                >
                  Edit profile
                </Button>
              </>
            ) : null
          }
          sx={{ mb: 3 }}
        />

        {loading ? (
          <Box sx={{ ...cardSx, p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Skeleton variant="circular" width={88} height={88} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="40%" height={30} />
                <Skeleton width="55%" />
              </Box>
            </Box>
            <Skeleton variant="rounded" height={52} sx={{ mt: 3 }} />
            <Skeleton variant="rounded" height={52} sx={{ mt: 2 }} />
          </Box>
        ) : (
          <>
            {saveSuccess && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 3 }}>
                Profile updated successfully!
              </Alert>
            )}

            <Box sx={cardSx}>
              <Box
                sx={{
                  height: { xs: 72, sm: 96 },
                  background: `linear-gradient(120deg, ${COLORS.MainColor}, ${alpha(
                    COLORS.MainColor,
                    0.55
                  )})`,
                }}
              />
              <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 1, sm: 2 },
                    mt: { xs: "-40px", sm: "-48px" },
                    mb: 3,
                  }}
                >
                  <Box sx={{ position: "relative", flexShrink: 0 }}>
                    <Avatar
                      src={shownImage || undefined}
                      alt={shownName || "Profile"}
                      onClick={openImagePicker}
                      sx={{
                        width: { xs: 80, sm: 96 },
                        height: { xs: 80, sm: 96 },
                        fontSize: "2rem",
                        fontWeight: 700,
                        bgcolor: COLORS.MainColor,
                        border: "4px solid",
                        borderColor: COLORS.WhiteBackground,
                        boxShadow: "0 6px 16px rgba(15, 23, 42, 0.15)",
                        cursor: editMode ? "pointer" : "default",
                      }}
                    >
                      {initial}
                    </Avatar>
                    {editMode && (
                      <Tooltip title="Change photo">
                        <IconButton
                          aria-label="change-image"
                          onClick={openImagePicker}
                          sx={{
                            position: "absolute",
                            right: 0,
                            bottom: 0,
                            width: 30,
                            height: 30,
                            bgcolor: COLORS.MainColor,
                            color: COLORS.WhiteText,
                            border: "2px solid",
                            borderColor: COLORS.WhiteBackground,
                            "&:hover": { bgcolor: COLORS.MainColor, opacity: 0.9 },
                          }}
                        >
                          <PhotoCameraOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {editMode && form.image && form.image !== DEFAULT_IMAGE && (
                      <Tooltip title="Remove photo">
                        <IconButton
                          aria-label="remove-image"
                          sx={{
                            position: "absolute",
                            right: -6,
                            top: 4,
                            bgcolor: "white",
                            border: "1px solid",
                            borderColor: "divider",
                            width: 28,
                            height: 28,
                            "&:hover": { bgcolor: "#fdecec" },
                          }}
                          onClick={() => {
                            try {
                              const maybeImage = (form as any).image;
                              if (
                                maybeImage &&
                                typeof maybeImage === "string" &&
                                maybeImage.startsWith("blob:")
                              ) {
                                URL.revokeObjectURL(maybeImage);
                              }
                            } catch (e) {
                              // noop
                            }

                            setForm((prev) => ({
                              ...prev,
                              image: DEFAULT_IMAGE,
                              imageFile: undefined,
                            }));
                            setRemoveImage(true);
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16, color: "error.main" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <input
                      id="profile-image-input"
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > MAX_IMAGE_SIZE_BYTES) {
                            toast.error("Image size must be less than 2MB.");
                            e.target.value = "";
                            return;
                          }

                          setRemoveImage(false);

                          const imagePreview = URL.createObjectURL(file);

                          setForm((prev) => ({
                            ...prev,
                            imageFile: file,
                            image: imagePreview,
                          }));
                        }
                      }}
                    />
                  </Box>
                  {/* Starts below the banner edge (avatar overlaps it by 48px). */}
                  <Box sx={{ minWidth: 0, pt: { sm: "64px" } }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.25rem",
                        color: COLORS.typographyColor,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {shownName || "Your name"}
                    </Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: "0.9rem" }}>
                      {(editMode ? form.email : profile.email) || "No email set"}
                    </Typography>
                  </Box>
                  {editMode && (
                    <Chip
                      label="Editing"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ ml: { sm: "auto" }, mt: { sm: "66px" }, fontWeight: 600 }}
                    />
                  )}
                </Box>

                <Box
                  component="form"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <CustomTextField
                    label="Full Name"
                    name="fullName"
                    value={editMode ? form.fullName : profile.fullName}
                    onChange={handleChange}
                    disabled={!editMode}
                    fullWidth
                    sx={fieldSx}
                  />
                  <CustomTextField
                    label="Email"
                    name="email"
                    value={editMode ? form.email : profile.email}
                    onChange={handleChange}
                    disabled={!editMode}
                    fullWidth
                    sx={fieldSx}
                  />
                  <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
                    <CountryCodePhoneField
                      countryCode={editMode ? form.country : profile.country}
                      phoneNumber={editMode ? form.phone : profile.phone}
                      onCountryCodeChange={(value) =>
                        setForm((prev) => ({ ...prev, country: value }))
                      }
                      onPhoneNumberChange={(value) =>
                        setForm((prev) => ({ ...prev, phone: value }))
                      }
                      disabled={!editMode}
                      compact={!editMode}
                    />
                  </Box>
                </Box>

                {editMode && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 1.5,
                      mt: 3,
                      flexDirection: { xs: "column-reverse", sm: "row" },
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleCancelEdit}
                      sx={{ ...buttonSx, minWidth: 120 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      disableElevation
                      disabled={saving}
                      onClick={handleSave}
                      startIcon={
                        saving ? <CircularProgress size={16} color="inherit" /> : undefined
                      }
                      sx={{ ...buttonSx, minWidth: 140 }}
                    >
                      {saving ? "Saving..." : "Save changes"}
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ ...cardSx, mt: 3 }}>
              <Typography
                sx={{
                  px: { xs: 2, sm: 3 },
                  pt: 2.5,
                  pb: 0.5,
                  fontWeight: 700,
                  fontSize: "1rem",
                  color: COLORS.typographyColor,
                }}
              >
                Security & notifications
              </Typography>
              <Box sx={actionRowSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                  <Box sx={rowIconSx}>
                    <LockOutlinedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                      Password
                    </Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: "0.82rem" }}>
                      {editMode
                        ? "Set a new password for your account."
                        : "Click Edit profile to change your password."}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  disabled={!editMode}
                  onClick={handleChangePassword}
                  sx={{ ...buttonSx, minWidth: 180 }}
                >
                  Change password
                </Button>
              </Box>
              <Divider sx={{ mx: { xs: 2, sm: 3 } }} />
              <Box sx={actionRowSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                  <Box sx={rowIconSx}>
                    <NotificationsOffOutlinedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                      Notifications on this device
                    </Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: "0.82rem" }}>
                      Stop backoffice push notifications in this browser.
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  disabled={disablingNotifications}
                  onClick={handleDisableNotificationsOnThisDevice}
                  startIcon={
                    disablingNotifications ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                  sx={{ ...buttonSx, minWidth: 180 }}
                >
                  {disablingNotifications ? "Disabling..." : "Disable notifications"}
                </Button>
              </Box>
            </Box>
          </>
        )}

        <GenericFormDialog
          open={passwordDialogOpen}
          title="Change Password"
          onClose={() => setPasswordDialogOpen(false)}
          onSubmit={handlePasswordDialogSubmit}
          fields={passwordFields}
          initialValues={{ logoutAllDevices: false }}
          submitLabel={changingPassword ? "Submitting..." : "Submit"}
        />
      </Box>
    </Box>
  );
};

export default Profile;
